// Updated DetailedDashboard.jsx
import React, { useEffect, useState } from "react";
import CountCard from "../UI/Dashboard/CountCard";
import { FaSolarPanel, FaUsers } from "react-icons/fa";
import { api_Routes } from "../tools/api_Routes";
import { Helper } from "../tools/Helper";
import { Box, CircularProgress } from "@mui/material";
import { MdMiscellaneousServices } from "react-icons/md";
import styled from "styled-components";
import { TbMenuOrder } from "react-icons/tb";
import {
  AccountBalance, AccountBalanceWallet,
  AdUnits,
  Balance,
  BalanceRounded,
  CurrencyExchange,
  PaymentOutlined,
  SellOutlined
} from "@mui/icons-material";
import DataTable from "../UI/Dashboard/DataTable/DataTable";
import {CgViewMonth} from "react-icons/cg"; // Import the new component

const Styles = styled.div`
  padding-bottom: 80px;
  
  .contain {
    @media (max-width: 1260px) {
      flex-direction: column;
      align-items: center;
      gap: 50px;
    }
  }
  
  .tables-container {
    display: flex;
    gap: 20px;
    margin-top: 40px;
    
    @media (max-width: 768px) {
      flex-direction: column;
    }
    
    > div {
      flex: 1;
      min-width: 0;
    }
  }
`;

const DetailedDashboard = () => {
  const [data, setData] = useState({});
  const [isLoading, setIsLoading] = useState(true);

  const getData = async () => {
    setIsLoading(true);
    const { response, message } = await Helper.Get({
      url: `${api_Routes.dashboard.getDetailedStatistics}`,
      hasToken: true,
    });
    if (response) {
      setData(response?.data);
    } else {
      setData({});
    }
    setIsLoading(false);
  };

  useEffect(() => {
    getData();
  }, []);

  // Table configurations
  const userTableConfig = {
    title: "Last Registered Users",
    columns: [
      { field: "id", headerName: "ID" },
      { field: "name", headerName: "Name" },
      { field: "email", headerName: "Email" },
      { field: "created_at", headerName: "Created at" },
    ],
    dateField: "created_at"
  };

  const panelTableConfig = {
    title: "Last Created Panels",
    columns: [
      { field: "smmpanel_id", headerName: "ID" },
      { field: "smmpanel_url", headerName: "Panel Url" },
      { field: "smmpanel_date", headerName: "Created at" },
    ],
    dateField: "smmpanel_date"
  };
  const orderTableConfig = {
    title: "Last Created Orders",
    columns: [
      { field: "request_id", headerName: "ID" },
      {
        field: "our_service.name ?? offer.title",
        headerName: "Service",
        isFallback: true // Optional flag for documentation
      },
      {
        field: "price",
        headerName: "Price",
        isPrice: true // Add this to format as currency
      },
      { field: "payment_method", headerName: "Payment Method" },
      { field: "status_payment", headerName: "Payment Status" },
      { field: "user.name", headerName: "User" },
      {
        field: "created_at",
        headerName: "Created at",
        isDate: true // Explicitly mark as date
      },
    ],
    dateField: "created_at"
  };
  const domainOrderTableConfig = {
    title: "Last Created Domain Order",
    columns: [
      { field: "id", headerName: "ID" },
      {
        field: "domains.domain",
        headerName: "Domain",
      },
      {
        field: "price",
        headerName: "Price",
        isPrice: true // Add this to format as currency
      },
      { field: "payment_method", headerName: "Payment Method" },
      { field: "status_payment", headerName: "Payment Status" },
      { field: "user.name", headerName: "User" },
      {
        field: "created_at",
        headerName: "Created at",
        isDate: true // Explicitly mark as date
      },
    ],
    dateField: "created_at"
  };
  const transactionTableConfig = {
    title: "Last Created Transaction",
    columns: [
      { field: "transaction_id", headerName: "ID" },
      {
        field: "amount",
        headerName: "Amount",
        isPrice: true // Add this to format as currency
      },
      { field: "gateway", headerName: "Gateway" },
      { field: "status", headerName: "Status" },
      {
        field: "created_at",
        headerName: "Created at",
        isDate: true // Explicitly mark as date
      },
    ],
    dateField: "created_at"
  };

  return isLoading ? (
      <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "calc(100dvh - 290px)",
          }}
      >
        <CircularProgress />
      </Box>
  ) : (
      <Styles>
        <div className="flex justify-around items-start gap-4 flex-wrap contain">
          <div>
            <CountCard
                title={"Active Panels"}
                count={data?.total_panels || 0}
                Icon={FaSolarPanel}
            />
          </div>
          <div>
            <CountCard
                title={"Active Users"}
                count={data?.total_users || 0}
                Icon={FaUsers}
            />
          </div>
          <div>
            <CountCard
                title={"Services"}
                count={data?.total_panel_services || 0}
                Icon={MdMiscellaneousServices}
            />
          </div>
          <div>
            <CountCard
                title={"Order"}
                count={data?.total_orders || 0}
                Icon={TbMenuOrder}
            />
          </div>
          <div>
            <CountCard
                title={"Users ads"}
                count={data?.total_users_ads || 0}
                Icon={AdUnits}
            />
          </div>
        </div>
        <div className="flex justify-around items-start gap-4 flex-wrap contain mt-3">
          <div>
            <CountCard
                title={"This Month Order"}
                count={data?.total_ThisMonthOrder || 0}
                Icon={CgViewMonth}
            />
          </div>
          <div>
            <CountCard
                title={"This Month Payed Order"}
                count={data?.total_ThisMonthPayedOrder || 0}
                Icon={PaymentOutlined}
            />
          </div>
          <div>
            <CountCard
                title={"This month order payment"}
                count={data?.total_order_payment || 0}
                exactValue={true}
                currency={'$'}
                Icon={CurrencyExchange}
            />
          </div>
          <div>
            <CountCard
                title={"Total Added Balance"}
                count={data?.total_added_balance || 0}
                exactValue={true}
                currency={'$'}
                Icon={AccountBalanceWallet}
            />
          </div>
          <div>
            <CountCard
                title={"Total this month added balance"}
                count={data?.total_this_month_added_balance || 0}
                exactValue={true}
                currency={'$'}
                Icon={AccountBalance}
            />
          </div>
        </div>

        <div className="tables-container">
          <DataTable
              title={userTableConfig.title}
              columns={userTableConfig.columns}
              data={data?.last_users || []}
              dateField={userTableConfig.dateField}
          />

          <DataTable
              title={panelTableConfig.title}
              columns={panelTableConfig.columns}
              data={data?.last_panels || []}
              dateField={panelTableConfig.dateField}
          />
        </div>

        <div className="tables-container">
          <DataTable
              title={orderTableConfig.title}
              columns={orderTableConfig.columns}
              data={data?.last_orders || []}
              dateField={orderTableConfig.dateField}
          />
          <DataTable
              title={domainOrderTableConfig.title}
              columns={domainOrderTableConfig.columns}
              data={data?.last_domain_orders || []}
              dateField={domainOrderTableConfig.dateField}
          />
        </div>
        <div className="tables-container">
          <DataTable
              title={transactionTableConfig.title}
              columns={transactionTableConfig.columns}
              data={data?.last_transaction || []}
              dateField={transactionTableConfig.dateField}
          />
        </div>
      </Styles>
  );
};

export default React.memo(DetailedDashboard);